import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Breadcrumb, Icon, Button, Segment, Header, Form, Input, Dropdown, Checkbox, Popup, Message, TextArea, Label, Accordion } from 'semantic-ui-react';
import { ChromePicker } from 'react-color';
import _ from 'lodash';
import Page from '../../components/Page';
import { post } from '../../util/api';
import { select_product } from '../../actions/product.actions';

/*
 * Mağaza tasarımı (whatsapp-shop "atelier" teması) ayarları.
 *
 * Yazdığı yerler:
 *   client_config.theme            → whatsapp-shop/themes (renk, font, metin, footer…)
 *   server_config.collectaction_ai → whatsapp-shop/lib/scripts.js (CollectAction AI script)
 *   server_config.dialog_tab_pid   → whatsapp-shop/lib/scripts.js (DialogTab canlı destek)
 * Sunucu tarafı (/shop/updateClientConfig, /shop/updateServerConfig) gönderilen anahtarı
 * mevcut ayarla birleştirir; yalnızca `theme` / `collectaction_ai` değişir.
 *
 * Boş bırakılan alan kaydedilmez: renklerde tema otomatik türetir, metinlerde temanın
 * varsayılanı kullanılır.
 */

const LOCALES = [
	{ key: 'tr', text: 'Türkçe', flag: 'tr' },
	{ key: 'en', text: 'English', flag: 'gb' },
	{ key: 'ru', text: 'Русский', flag: 'ru' },
	{ key: 'fr', text: 'Français', flag: 'fr' },
	{ key: 'ar', text: 'العربية', flag: 'sa' },
];

// Varsayılanlar whatsapp-shop/themes/atelier/atelier.scss ile aynı (önizleme için).
const DEFAULTS = {
	brand: '#8C0505',
	bg: '#FBF9F7',
	surface: '#FFFFFF',
	surface_2: '#F6F2EE',
	ink: '#16130F',
	ink_2: '#3D3833',
	muted: '#7A736C',
	muted_2: '#A8A099',
	line: '#E9E4DE',
	line_2: '#F2EEE9',
	wa: '#1FA855',
	on_wa: '#FFFFFF',
};

const NEUTRALS = {
	warm: {},
	neutral: {
		ink: '#111111',
		ink_2: '#3a3a3a',
		muted: '#737373',
		muted_2: '#a3a3a3',
		line: '#e5e5e5',
		line_2: '#f0f0f0',
		bg: '#fafafa',
		surface_2: '#f4f4f4',
	},
	cool: { ink: '#111418', ink_2: '#3a3f47', muted: '#6b7280', muted_2: '#9ca3af', line: '#e5e7eb', line_2: '#f1f2f4', bg: '#f8f9fa', surface_2: '#f3f4f6' },
};

const ADVANCED_COLORS = [
	{
		group: 'Marka',
		items: [
			['brand_dark', 'Hover / koyu ton'],
			['brand_light', 'Açık ton'],
			['brand_50', 'Çok açık zemin'],
			['brand_100', 'Açık zemin'],
			['on_brand', 'Marka rengi üstündeki yazı'],
		],
	},
	{
		group: 'Zemin',
		items: [
			['bg', 'Sayfa zemini'],
			['surface', 'Kart / panel zemini'],
			['surface_2', 'İkincil zemin'],
			['header_bg', 'Header zemini (yarı saydam olabilir)'],
		],
	},
	{
		group: 'Yazı',
		items: [
			['ink', 'Ana yazı ve koyu butonlar'],
			['ink_2', 'İkincil yazı'],
			['muted', 'Soluk yazı'],
			['muted_2', 'Daha soluk yazı'],
			['on_ink', 'Koyu buton üstündeki yazı'],
		],
	},
	{
		group: 'Çizgi',
		items: [
			['line', 'Kenarlık'],
			['line_2', 'İnce ayraç'],
		],
	},
	{
		group: 'WhatsApp butonu',
		items: [
			['wa', 'Buton rengi'],
			['wa_dark', 'Buton koyu tonu'],
			['on_wa', 'Buton yazısı'],
		],
	},
	{ group: 'Diğer', items: [['shadow', 'Gölge / karartma rengi']] },
];

const SANS_FONTS = [
	'Inter',
	'Poppins',
	'Montserrat',
	'Manrope',
	'DM Sans',
	'Work Sans',
	'Nunito Sans',
	'Lato',
	'Open Sans',
	'Roboto',
	'Rubik',
	'Jost',
	'Outfit',
];
const SERIF_FONTS = [
	'Cormorant Garamond',
	'Playfair Display',
	'Lora',
	'Libre Baskerville',
	'DM Serif Display',
	'Bodoni Moda',
	'Prata',
	'Marcellus',
	'EB Garamond',
	'Cinzel',
];

const FOOTER_LINKS = [
	['instagram', 'Instagram', 'kullanıcı adı ya da link'],
	['telegram', 'Telegram', 'kullanıcı adı ya da link'],
	['whatsapp', 'WhatsApp', 'numara (boşsa mağaza numarası)'],
	['facebook', 'Facebook', 'kullanıcı adı ya da link'],
	['tiktok', 'TikTok', 'kullanıcı adı ya da link'],
	['youtube', 'YouTube', 'kanal adı ya da link'],
	['website', 'Web sitesi', 'adres'],
	['phone', 'Telefon', '+90 …'],
	['email', 'E-posta', 'info@…'],
	['map_url', 'Harita linki', 'Google Maps linki'],
];

// ---- renk yardımcıları (whatsapp-shop/themes/atelier/helpers.js tokenStyle ile aynı kurallar) ----
function hexToRgb(hex) {
	let h = String(hex || '').replace('#', '');
	if (h.length === 3) h = h.replace(/(.)/g, '$1$1');
	if (!/^[0-9a-f]{6}$/i.test(h)) return null;
	return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
function mix(rgb, target, amount) {
	return (
		'#' +
		rgb
			.map((c, i) =>
				Math.round(c + (target[i] - c) * amount)
					.toString(16)
					.padStart(2, '0')
			)
			.join('')
	);
}
function luminance(rgb) {
	return (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) / 255;
}

/** Önizleme için son renkler: verilen > gri seti > varsayılan, türetilenler türetilir. */
function resolveColors(tokens, fallbackBrand) {
	let preset = NEUTRALS[tokens.neutral] || {};
	let pick = (k) => tokens[k] || preset[k] || DEFAULTS[k];
	let brandHex = tokens.brand || fallbackBrand || DEFAULTS.brand;
	let brand = hexToRgb(brandHex) || hexToRgb(DEFAULTS.brand);
	let dark = luminance(brand) < 0.12;
	let ink = hexToRgb(pick('ink'));
	return {
		brand: brandHex,
		brand_dark: tokens.brand_dark || (dark ? mix(brand, [255, 255, 255], 0.18) : mix(brand, [0, 0, 0], 0.21)),
		brand_100: tokens.brand_100 || mix(brand, [255, 255, 255], 0.86),
		on_brand: tokens.on_brand || (luminance(brand) > 0.6 ? '#111111' : '#ffffff'),
		bg: pick('bg'),
		surface: pick('surface'),
		surface_2: pick('surface_2'),
		ink: pick('ink'),
		ink_2: pick('ink_2'),
		muted: pick('muted'),
		line: pick('line'),
		on_ink: tokens.on_ink || (ink && luminance(ink) > 0.6 ? '#111111' : '#ffffff'),
		wa: tokens.wa || DEFAULTS.wa,
		on_wa: tokens.on_wa || DEFAULTS.on_wa,
	};
}

/** Boş metin / boş nesne / boş liste alanlarını at (tema varsayılanı devreye girsin). */
function compact(value) {
	if (Array.isArray(value)) {
		let arr = value.map(compact).filter((v) => v !== undefined);
		return arr.length ? arr : undefined;
	}
	if (value && typeof value === 'object') {
		let out = {};
		Object.keys(value).forEach((k) => {
			let v = compact(value[k]);
			if (v !== undefined) out[k] = v;
		});
		return Object.keys(out).length ? out : undefined;
	}
	if (typeof value === 'string') return value.trim() ? value.trim() : undefined;
	if (value === null || value === undefined) return undefined;
	return value;
}

function useGoogleFonts(fonts) {
	useEffect(() => {
		let names = _.compact(_.uniq(fonts));
		if (!names.length) return;
		let id = 'design-preview-fonts';
		let link = document.getElementById(id) || document.createElement('link');
		link.id = id;
		link.rel = 'stylesheet';
		link.href =
			'https://fonts.googleapis.com/css2?' +
			names.map((n) => 'family=' + encodeURIComponent(n).replace(/%20/g, '+') + ':wght@400;600;700').join('&') +
			'&display=swap';
		if (!link.parentNode) document.head.appendChild(link);
	}, [fonts.join('|')]);
}

// ---- küçük bileşenler ----
const UPLOAD_URL = 'https://uploads.yapaytech.com/flow/uploadPermImage';

async function uploadImage(file) {
	let formData = new FormData();
	formData.append('files[]', file);
	let resp = await fetch(UPLOAD_URL, { method: 'post', body: formData });
	let data = await resp.json();
	return (data && data.files && data.files[0] && data.files[0].url) || null;
}

function LogoField({ value, height, onChange, onHeight }) {
	let [loading, setLoading] = useState(false);
	let [error, setError] = useState(false);
	async function onFile(e) {
		let file = e.target.files && e.target.files[0];
		e.target.value = '';
		if (!file) return;
		setLoading(true);
		setError(false);
		try {
			let url = await uploadImage(file);
			if (url) onChange(url);
			else setError(true);
		} catch (err) {
			setError(true);
		}
		setLoading(false);
	}
	return (
		<div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
			<div
				style={{
					width: 180,
					height: 64,
					border: '1px dashed rgba(0,0,0,.25)',
					borderRadius: 6,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					background: '#fafafa',
					overflow: 'hidden',
				}}
			>
				{value ? (
					<img src={value} alt="logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
				) : (
					<span style={{ fontSize: 12, color: '#999' }}>Logo yok</span>
				)}
			</div>
			<input id="design-logo-upload" type="file" accept="image/*" style={{ display: 'none' }} onChange={onFile} />
			<Button as="label" htmlFor="design-logo-upload" size="small" basic icon="upload" loading={loading} content={value ? 'Değiştir' : 'Logo yükle'} />
			{value ? <Button size="small" basic icon="trash" content="Kaldır" onClick={() => onChange('')} /> : null}
			<Input
				size="small"
				type="number"
				min={20}
				max={80}
				style={{ width: 150 }}
				label={{ basic: true, content: 'px' }}
				labelPosition="right"
				placeholder="Yükseklik 40"
				value={height || ''}
				onChange={(e, { value: v }) => onHeight(v ? parseInt(v, 10) || '' : '')}
			/>
			{error ? <span style={{ color: '#db2828', fontSize: 12 }}>Yükleme başarısız, tekrar deneyin.</span> : null}
		</div>
	);
}

function ColorField({ label, value, placeholder, onChange, hint }) {
	return (
		<div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
			<Popup
				on="click"
				position="bottom left"
				style={{ padding: 0, borderRadius: 0 }}
				trigger={
					<div
						title="Renk seç"
						style={{
							width: 44,
							height: 30,
							borderRadius: 4,
							border: '1px solid rgba(0,0,0,.2)',
							cursor: 'pointer',
							flex: '0 0 auto',
							background: value || placeholder || '#fff',
							backgroundImage: value ? 'none' : 'repeating-linear-gradient(45deg,#0000 0 6px,#0001 6px 12px)',
						}}
					/>
				}
				content={<ChromePicker disableAlpha color={value || placeholder || '#ffffff'} onChange={({ hex }) => onChange(hex)} />}
			/>
			<Input
				size="small"
				style={{ width: 110 }}
				value={value || ''}
				placeholder={placeholder || 'otomatik'}
				onChange={(e, { value: v }) => onChange(v)}
			/>
			{value ? <Button size="mini" basic icon="undo" title="Otomatiğe döndür" onClick={() => onChange('')} /> : null}
			<div style={{ fontSize: 13 }}>
				{label}
				{hint ? <div style={{ fontSize: 11.5, color: '#888', fontWeight: 400 }}>{hint}</div> : null}
			</div>
		</div>
	);
}

function LocaleTabs({ value, onChange, filled }) {
	return (
		<Button.Group size="small" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
			{LOCALES.map((l) => (
				<Button
					key={l.key}
					active={value === l.key}
					onClick={() => onChange(l.key)}
					basic={value !== l.key}
					color={value === l.key ? 'blue' : undefined}
				>
					<i className={`${l.flag} flag`} />
					{l.key.toUpperCase()}
					{filled && filled(l.key) ? <Icon name="check" style={{ marginLeft: 4, marginRight: 0 }} /> : null}
				</Button>
			))}
		</Button.Group>
	);
}

function Preview({ theme, shop, locale }) {
	let tokens = theme.tokens || {};
	let c = resolveColors(tokens, shop.client_config && shop.client_config.header_color);
	let sans = `"${tokens.font_sans || 'Inter'}", sans-serif`;
	let serif = `"${tokens.font_serif || 'Cormorant Garamond'}", Georgia, serif`;
	let strip = (theme.topstrip && theme.topstrip[locale]) || [];
	let hero = (theme.hero && theme.hero[locale]) || {};
	let bm = theme.brandmark || {};
	return (
		<div style={{ border: `1px solid ${c.line}`, borderRadius: 14, overflow: 'hidden', background: c.bg, fontFamily: sans, color: c.ink }}>
			{strip.length ? (
				<div
					style={{
						background: c.brand,
						color: c.on_brand,
						fontSize: 10.5,
						letterSpacing: '.06em',
						textTransform: 'uppercase',
						textAlign: 'center',
						padding: '7px 10px',
						fontWeight: 600,
					}}
				>
					{strip.filter(Boolean).join('  ·  ')}
				</div>
			) : null}
			<div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: `1px solid ${c.line}`, background: c.bg }}>
				{theme.logo ? (
					<img
						src={theme.logo}
						alt="logo"
						style={{ height: Math.min(theme.logo_height || 40, 60) * 0.8, width: 'auto', maxWidth: 160, objectFit: 'contain' }}
					/>
				) : null}
				{!theme.logo || bm.line1 || bm.line2 ? (
					<>
						<span style={{ fontFamily: serif, fontWeight: 700, fontSize: 21, color: c.brand, fontVariantNumeric: 'lining-nums' }}>
							{bm.line1 || (!theme.logo && shop.client_config.title) || ''}
						</span>
						{bm.line2 ? <span style={{ fontSize: 9, letterSpacing: '.3em', textTransform: 'uppercase', color: c.ink_2 }}>{bm.line2}</span> : null}
					</>
				) : null}
				<div style={{ flex: 1, height: 28, borderRadius: 99, background: c.surface_2, marginLeft: 8 }} />
			</div>
			<div style={{ padding: 14 }}>
				<div style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 12, padding: '12px 14px', marginBottom: 12 }}>
					<div style={{ fontFamily: serif, fontSize: 20, fontWeight: 600, lineHeight: 1.15 }}>
						{hero.title || 'Sezonun tüm markaları,'}
						<br />
						<em style={{ color: c.brand }}>{hero.em || 'tek katalogda.'}</em>
					</div>
					<div style={{ fontSize: 11.5, color: c.muted, marginTop: 4 }}>
						{hero.text || 'Modelleri sepete ekleyin, siparişinizi WhatsApp ile gönderin.'}
					</div>
				</div>
				<div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
					<span style={{ background: c.ink, color: c.on_ink, borderRadius: 99, padding: '4px 11px', fontSize: 11.5, fontWeight: 600 }}>
						Seçili marka
					</span>
					<span style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 99, padding: '4px 11px', fontSize: 11.5 }}>
						Diğer marka
					</span>
				</div>
				<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
					<div style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 12, overflow: 'hidden' }}>
						<div style={{ height: 70, background: c.surface_2, position: 'relative' }}>
							<span
								style={{
									position: 'absolute',
									top: 7,
									left: 7,
									background: c.brand,
									color: c.on_brand,
									borderRadius: 99,
									padding: '2px 8px',
									fontSize: 9.5,
									fontWeight: 700,
								}}
							>
								YENİ
							</span>
						</div>
						<div style={{ padding: 9 }}>
							<div style={{ fontSize: 9.5, color: c.muted, textTransform: 'uppercase', letterSpacing: '.06em' }}>Marka · 12345</div>
							<div style={{ fontWeight: 700, margin: '3px 0 7px' }}>$45</div>
							<div
								style={{
									background: c.brand,
									color: c.on_brand,
									borderRadius: 99,
									padding: '6px 0',
									textAlign: 'center',
									fontSize: 11.5,
									fontWeight: 600,
								}}
							>
								Sepete Ekle
							</div>
						</div>
					</div>
					<div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'flex-end' }}>
						<div style={{ background: c.brand_100, borderRadius: 10, padding: '8px 10px', fontSize: 11.5 }}>
							Toplam <b style={{ color: c.brand, float: 'right' }}>$180</b>
						</div>
						<div
							style={{ background: c.wa, color: c.on_wa, borderRadius: 99, padding: '8px 0', textAlign: 'center', fontSize: 12, fontWeight: 700 }}
						>
							WhatsApp ile Gönder
						</div>
					</div>
				</div>
			</div>
			<div style={{ borderTop: `1px solid ${c.line}`, background: c.surface, padding: '10px 14px', fontSize: 11, color: c.muted }}>
				© {new Date().getFullYear()} {shop.client_config.title || ''}
			</div>
		</div>
	);
}

// ---- sayfa ----
function Design({ shop, dispatch }) {
	let initial = (shop.client_config && shop.client_config.theme) || {};
	let initialAi = (shop.server_config && shop.server_config.collectaction_ai) || {};
	let [theme, setTheme] = useState(() => _.merge({ tokens: {}, brandmark: {}, topstrip: {}, hero: {}, footer: {}, features: {} }, _.cloneDeep(initial)));
	let [status, setStatus] = useState(!initial.name ? 'off' : initial.preview ? 'test' : 'live');
	let [dialogTab, setDialogTab] = useState(String((shop.server_config && shop.server_config.dialog_tab_pid) || ''));
	let [ai, setAi] = useState({ pid: initialAi.pid || '', uid: initialAi.uid || '', ecommerce_type: initialAi.ecommerce_type || '' });
	let [locale, setLocale] = useState('tr');
	let [advanced, setAdvanced] = useState(false);
	let [saving, setSaving] = useState(false);
	let [result, setResult] = useState(null);

	let tokens = theme.tokens || {};
	useGoogleFonts([tokens.font_sans || 'Inter', tokens.font_serif || 'Cormorant Garamond']);

	let set = (path, value) => {
		setResult(null);
		setTheme((cur) => {
			let next = _.cloneDeep(cur);
			_.set(next, path, value);
			return next;
		});
	};

	let keyword = shop.keyword || '';
	let shopUrl = shop.domain ? `https://${shop.domain}` : keyword ? `https://${keyword}.whatsapp-shop.com` : '';

	let fontOptions = (list, current) =>
		_.uniq(_.compact([current].concat(list))).map((f) => ({ key: f, value: f, text: f, content: <span style={{ fontFamily: `"${f}"` }}>{f}</span> }));
	let serifFonts = useMemo(() => fontOptions(SERIF_FONTS, tokens.font_serif), [tokens.font_serif]);
	let sansFonts = useMemo(() => fontOptions(SANS_FONTS, tokens.font_sans), [tokens.font_sans]);
	useGoogleFonts(SANS_FONTS.concat(SERIF_FONTS));

	async function save() {
		setSaving(true);
		setResult(null);
		let out = compact(_.omit(theme, ['name', 'preview'])) || {};
		// Şerit satırları: boş satırlar atılır.
		if (out.topstrip)
			out.topstrip = _.pickBy(
				_.mapValues(out.topstrip, (lines) => _.compact(lines)),
				(l) => l.length
			);
		let saved = Object.assign({ name: status === 'off' ? '' : 'atelier' }, status === 'test' ? { preview: true } : {}, out);
		let aiOut = compact(ai);
		let scripts = { collectaction_ai: aiOut && aiOut.pid ? aiOut : null, dialog_tab_pid: String(dialogTab).trim() || null };
		try {
			let r1 = await post('/shop/updateClientConfig', { client_config: { theme: saved } });
			let r2 = await post('/shop/updateServerConfig', { server_config: scripts });
			let ok = r1.data && r1.data.success && r2.data && r2.data.success;
			if (ok) {
				dispatch(
					select_product(
						Object.assign({}, shop, {
							client_config: Object.assign({}, shop.client_config, { theme: saved }),
							server_config: Object.assign({}, shop.server_config, scripts),
						})
					)
				);
			}
			setResult(ok ? 'ok' : 'error');
		} catch (e) {
			setResult('error');
		}
		setSaving(false);
	}

	let strip = (theme.topstrip && theme.topstrip[locale]) || [];
	let stripLines = [0, 1, 2].map((i) => strip[i] || '');

	return (
		<Page
			header={
				<>
					<Breadcrumb size="big">
						<Breadcrumb.Section>
							<Icon name="paint brush" />
							Tasarım
						</Breadcrumb.Section>
					</Breadcrumb>
					<Button style={{ position: 'absolute', right: '30px', margin: '15px' }} color="blue" onClick={save} loading={saving}>
						{result === 'ok' ? 'Kaydedildi' : result === 'error' ? 'Hata — tekrar dene' : 'Kaydet'}
					</Button>
				</>
			}
		>
			<div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', padding: '0 20px 40px', flexWrap: 'wrap' }}>
				<div style={{ flex: '1 1 560px', minWidth: 0 }}>
					{/* DURUM */}
					<Segment>
						<Header as="h4">Yeni tasarım</Header>
						<Form>
							<Form.Group inline>
								<Form.Radio label="Kapalı (eski görünüm)" checked={status === 'off'} onChange={() => setStatus('off')} />
								<Form.Radio label="Test — sadece test linkiyle" checked={status === 'test'} onChange={() => setStatus('test')} />
								<Form.Radio label="Yayında — herkese" checked={status === 'live'} onChange={() => setStatus('live')} />
							</Form.Group>
						</Form>
						{status === 'test' && shopUrl ? (
							<Message info size="small">
								Müşteriler eski görünümü görür. Test etmek için:{' '}
								<a href={`${shopUrl}/?test_thema=true`} target="_blank" rel="noopener noreferrer">
									{shopUrl}/?test_thema=true
								</a>
								<br />
								Kapatmak için <code>?test_thema=false</code>.
							</Message>
						) : null}
					</Segment>

					{/* RENKLER */}
					<Segment>
						<Header as="h4">Renkler</Header>
						<ColorField
							label="Ana renk"
							hint="Kampanya şeridi, butonlar, rozetler, fiyat vurguları. Diğer tonlar bundan türetilir."
							value={tokens.brand}
							placeholder={(shop.client_config && shop.client_config.header_color) || DEFAULTS.brand}
							onChange={(v) => set('tokens.brand', v)}
						/>
						<Form style={{ margin: '6px 0 4px' }}>
							<Form.Field inline>
								<label style={{ fontWeight: 600 }}>Gri tonlar</label>
								<Dropdown
									selection
									value={tokens.neutral || 'warm'}
									options={[
										{ key: 'warm', value: 'warm', text: 'Sıcak (bej) — varsayılan' },
										{ key: 'neutral', value: 'neutral', text: 'Nötr gri — siyah/beyaz markalar' },
										{ key: 'cool', value: 'cool', text: 'Soğuk gri — lacivert/yeşil markalar' },
									]}
									onChange={(e, { value }) => set('tokens.neutral', value === 'warm' ? '' : value)}
								/>
							</Form.Field>
						</Form>
						<Accordion>
							<Accordion.Title active={advanced} onClick={() => setAdvanced(!advanced)}>
								<Icon name="dropdown" />
								Tüm renkler (isteğe bağlı — boş olanlar otomatik)
							</Accordion.Title>
							<Accordion.Content active={advanced}>
								{ADVANCED_COLORS.map((g) => (
									<div key={g.group} style={{ marginBottom: 12 }}>
										<div
											style={{
												fontSize: 11,
												letterSpacing: '.1em',
												textTransform: 'uppercase',
												color: '#999',
												fontWeight: 700,
												margin: '8px 0',
											}}
										>
											{g.group}
										</div>
										{g.items.map(([key, label]) => (
											<ColorField
												key={key}
												label={label}
												value={tokens[key]}
												placeholder={DEFAULTS[key]}
												onChange={(v) => set(`tokens.${key}`, v)}
											/>
										))}
									</div>
								))}
							</Accordion.Content>
						</Accordion>
					</Segment>

					{/* YAZI */}
					<Segment>
						<Header as="h4">Yazı tipleri ve logo</Header>
						<Form>
							<Form.Field>
								<label>Logo (sol üst)</label>
								<LogoField
									value={theme.logo}
									height={theme.logo_height}
									onChange={(v) => set('logo', v)}
									onHeight={(v) => set('logo_height', v)}
								/>
							</Form.Field>
							<Form.Group widths="equal">
								<Form.Field>
									<label>Metin yazı tipi</label>
									<Dropdown
										selection
										search
										allowAdditions
										additionLabel="Google Fonts adı: "
										value={tokens.font_sans || 'Inter'}
										options={sansFonts}
										onChange={(e, { value }) => set('tokens.font_sans', value === 'Inter' ? '' : value)}
									/>
								</Form.Field>
								<Form.Field>
									<label>Başlık yazı tipi</label>
									<Dropdown
										selection
										search
										allowAdditions
										additionLabel="Google Fonts adı: "
										value={tokens.font_serif || 'Cormorant Garamond'}
										options={serifFonts}
										onChange={(e, { value }) => set('tokens.font_serif', value === 'Cormorant Garamond' ? '' : value)}
									/>
								</Form.Field>
							</Form.Group>
							<Form.Group widths="equal">
								<Form.Input
									label="Logo yazısı (1. satır)"
									placeholder={(shop.client_config && shop.client_config.title) || 'boşsa mağaza logosu'}
									value={(theme.brandmark && theme.brandmark.line1) || ''}
									onChange={(e, { value }) => set('brandmark.line1', value)}
								/>
								<Form.Input
									label="Logo yazısı (2. satır, küçük)"
									placeholder="ör. Xclusive"
									value={(theme.brandmark && theme.brandmark.line2) || ''}
									onChange={(e, { value }) => set('brandmark.line2', value)}
								/>
							</Form.Group>
							<div style={{ fontSize: 12, color: '#888', marginTop: -6 }}>
								Logo yüklendiyse logo yazısının soluna konur (önerilen 40×40, kare); logo yazısı boşsa yalnızca logo görünür. İkisi de boşsa
								mağaza logosu (navbar logosu) gösterilir.
							</div>
						</Form>
					</Segment>

					{/* DİLE GÖRE METİNLER */}
					<Segment>
						<Header as="h4">Metinler (dile göre)</Header>
						<LocaleTabs
							value={locale}
							onChange={setLocale}
							filled={(l) => _.some(_.get(theme, ['topstrip', l])) || _.some(_.values(_.get(theme, ['hero', l]) || {}))}
						/>
						<Form>
							<label style={{ fontWeight: 700, fontSize: 13 }}>Kampanya şeridi (üstteki renkli bar)</label>
							{stripLines.map((line, i) => (
								<Form.Input
									key={i}
									style={{ marginTop: 6 }}
									placeholder={
										i === 0
											? 'ör. Toptan Katalog · Butik & Mağaza Alımları'
											: i === 1
												? 'ör. Fiyatlar USD (mobilde gizlenir)'
												: 'ör. Kargo: aynı gün hazırlık'
									}
									value={line}
									onChange={(e, { value }) => {
										let next = stripLines.slice();
										next[i] = value;
										set(['topstrip', locale], next);
									}}
								/>
							))}
							<div style={{ fontSize: 12, color: '#888', margin: '2px 0 14px' }}>
								Boş bırakılan dilde İngilizce gösterilir; hiç doldurulmazsa şerit görünmez. Kampanya CollectAction ile yönetilecekse burayı boş
								bırakın.
							</div>
							<label style={{ fontWeight: 700, fontSize: 13 }}>Giriş (hero) metni</label>
							<Form.Group widths="equal" style={{ marginTop: 6 }}>
								<Form.Input
									placeholder="Sezonun tüm markaları,"
									value={_.get(theme, ['hero', locale, 'title']) || ''}
									onChange={(e, { value }) => set(['hero', locale, 'title'], value)}
								/>
								<Form.Input
									placeholder="tek katalogda. (renkli satır)"
									value={_.get(theme, ['hero', locale, 'em']) || ''}
									onChange={(e, { value }) => set(['hero', locale, 'em'], value)}
								/>
							</Form.Group>
							<Form.Field
								control={TextArea}
								rows={2}
								placeholder="Açıklama (boşsa temanın varsayılan metni)"
								value={_.get(theme, ['hero', locale, 'text']) || ''}
								onChange={(e, { value }) => set(['hero', locale, 'text'], value)}
							/>
							<label style={{ fontWeight: 700, fontSize: 13 }}>Footer</label>
							<Form.Group widths="equal" style={{ marginTop: 6 }}>
								<Form.Input
									placeholder="Adres"
									value={_.get(theme, ['footer', 'address', locale]) || ''}
									onChange={(e, { value }) => set(['footer', 'address', locale], value)}
								/>
								<Form.Input
									placeholder="Kısa açıklama"
									value={_.get(theme, ['footer', 'text', locale]) || ''}
									onChange={(e, { value }) => set(['footer', 'text', locale], value)}
								/>
							</Form.Group>
						</Form>
					</Segment>

					{/* FOOTER LİNKLERİ */}
					<Segment>
						<Header as="h4">Footer linkleri ve iletişim</Header>
						<Form>
							<Form.Group widths="equal" style={{ flexWrap: 'wrap' }}>
								{FOOTER_LINKS.map(([key, label, ph]) => (
									<Form.Input
										key={key}
										style={{ minWidth: 220, marginBottom: 10 }}
										label={label}
										placeholder={ph}
										value={_.get(theme, ['footer', key]) || ''}
										onChange={(e, { value }) => set(['footer', key], value)}
									/>
								))}
							</Form.Group>
						</Form>
					</Segment>

					{/* ÖZELLİKLER */}
					<Segment>
						<Header as="h4">Özellikler</Header>
						<Form>
							<Form.Field>
								<Checkbox
									toggle
									label="Çoklu seçim (birden çok ürünü işaretleyip sepete ekleme)"
									checked={_.get(theme, 'features.multi_select') !== false}
									onChange={(e, { checked }) => set('features.multi_select', checked)}
								/>
							</Form.Field>
							<Form.Field>
								<Checkbox
									toggle
									label="Girişte marka / model / yeni sayaçları"
									checked={_.get(theme, 'features.hero_stats') !== false}
									onChange={(e, { checked }) => set('features.hero_stats', checked)}
								/>
							</Form.Field>
							<Form.Field>
								<Checkbox
									toggle
									label="Sepette “Link kopyala” butonu"
									checked={_.get(theme, 'features.copy_link') !== false}
									onChange={(e, { checked }) => set('features.copy_link', checked)}
								/>
							</Form.Field>
							<Form.Input
								inline
								type="number"
								min={4}
								max={60}
								label="Marka bölümünde ilk gösterilen ürün sayısı"
								placeholder="12"
								value={theme.page_size || ''}
								onChange={(e, { value }) => set('page_size', value ? parseInt(value, 10) || '' : '')}
							/>
						</Form>
					</Segment>

					{/* SCRIPT'LER */}
					<Segment>
						<Header as="h4">
							Script'ler (DialogTab / CollectAction)
							<Header.Subheader>Eski ve yeni tasarımda yüklenir; pid boşsa yüklenmez.</Header.Subheader>
						</Header>
						<Form>
							<Form.Input
								label="DialogTab pid (canlı destek)"
								placeholder="DialogTab ürün pid'i"
								value={dialogTab}
								onChange={(e, { value }) => {
									setResult(null);
									setDialogTab(value);
								}}
							/>
							<div style={{ fontWeight: 700, fontSize: 13, margin: '14px 0 6px' }}>CollectAction AI (kampanya / AI script)</div>
							<Form.Group widths="equal">
								<Form.Input
									label="pid"
									placeholder="9348762c-…"
									value={ai.pid}
									onChange={(e, { value }) => setAi(Object.assign({}, ai, { pid: value }))}
								/>
								<Form.Input
									label="uid"
									placeholder="ör. 4866"
									value={ai.uid}
									onChange={(e, { value }) => setAi(Object.assign({}, ai, { uid: value }))}
								/>
								<Form.Input
									label="e-ticaret tipi"
									placeholder="ör. ticimax"
									value={ai.ecommerce_type}
									onChange={(e, { value }) => setAi(Object.assign({}, ai, { ecommerce_type: value }))}
								/>
							</Form.Group>
						</Form>
					</Segment>
				</div>

				{/* ÖNİZLEME */}
				<div style={{ flex: '0 1 380px', position: 'sticky', top: 20 }}>
					<div style={{ fontSize: 11, letterSpacing: '.1em', textTransform: 'uppercase', color: '#999', fontWeight: 700, marginBottom: 8 }}>
						Önizleme <Label size="mini">{locale.toUpperCase()}</Label>
					</div>
					<Preview theme={theme} shop={shop} locale={locale} />
					<div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
						Yaklaşık görünüm. Gerçeği için kaydedip {status === 'live' ? 'mağazayı' : 'test linkini'} açın.
					</div>
				</div>
			</div>
		</Page>
	);
}

export default connect((state) => ({ shop: state.product.shop }))(Design);
